import {
  Bell, MessageSquare, CalendarCheck, CalendarDays, UserPlus,
  ClipboardList, Flame, UtensilsCrossed, Dumbbell, Camera,
  LifeBuoy, Megaphone, Sparkles, CreditCard, Pill,
} from "lucide-react";

const TYPE_META = {
  new_message: {
    icon: MessageSquare,
    color: "bg-blue-100 text-blue-600",
    label: "Message",
  },
  new_session: {
    icon: CalendarCheck,
    color: "bg-indigo-100 text-indigo-600",
    label: "Séance",
  },
  session_reminder: {
    icon: CalendarDays,
    color: "bg-indigo-100 text-indigo-600",
    label: "Rappel séance",
  },
  session_reminder_1h: {
    icon: CalendarDays,
    color: "bg-orange-100 text-orange-600",
    label: "Séance imminente",
  },
  new_student: {
    icon: UserPlus,
    color: "bg-emerald-100 text-emerald-600",
    label: "Nouvel élève",
  },
  checkin_new: {
    icon: ClipboardList,
    color: "bg-violet-100 text-violet-600",
    label: "Check-in",
  },
  checkin_comment: {
    icon: MessageSquare,
    color: "bg-violet-100 text-violet-600",
    label: "Commentaire",
  },
  checkin_weekly: {
    icon: ClipboardList,
    color: "bg-amber-100 text-amber-600",
    label: "Bilan hebdo",
  },
  checkin_reminder: {
    icon: Flame,
    color: "bg-orange-100 text-orange-600",
    label: "Rappel",
  },
  daily_checkin: {
    icon: Flame,
    color: "bg-rose-100 text-rose-600",
    label: "Check-in quotidien",
  },
  new_plan: {
    icon: UtensilsCrossed,
    color: "bg-green-100 text-green-600",
    label: "Nouveau plan",
  },
  new_diet_plan: {
    icon: UtensilsCrossed,
    color: "bg-green-100 text-green-600",
    label: "Plan alimentaire",
  },
  new_training_plan: {
    icon: Dumbbell,
    color: "bg-sky-100 text-sky-600",
    label: "Programme",
  },
  workout_loads_tomorrow: {
    icon: Dumbbell,
    color: "bg-orange-100 text-orange-700",
    label: "Charges demain",
  },
  photo_request: {
    icon: Camera,
    color: "bg-pink-100 text-pink-600",
    label: "Photos demandées",
  },
  photo_reminder: {
    icon: Camera,
    color: "bg-pink-100 text-pink-600",
    label: "Rappel photos",
  },
  support_reply: {
    icon: LifeBuoy,
    color: "bg-cyan-100 text-cyan-600",
    label: "Support",
  },
  admin_announcement: {
    icon: Megaphone,
    color: "bg-purple-100 text-purple-600",
    label: "Annonce",
  },
  ai_coach: {
    icon: Sparkles,
    color: "bg-fuchsia-100 text-fuchsia-600",
    label: "Coach IA",
  },
  payment: {
    icon: CreditCard,
    color: "bg-lime-100 text-lime-700",
    label: "Paiement",
  },
  payment_received: {
    icon: CreditCard,
    color: "bg-lime-100 text-lime-700",
    label: "Paiement reçu",
  },
  payment_failed: {
    icon: CreditCard,
    color: "bg-red-100 text-red-600",
    label: "Paiement",
  },
  meal_log: {
    icon: ClipboardList,
    color: "bg-amber-100 text-amber-700",
    label: "Repas",
  },
  supplement_plan: {
    icon: Pill,
    color: "bg-teal-100 text-teal-600",
    label: "Compléments",
  },
};

const DEFAULT_META = {
  icon: Bell,
  color: "bg-slate-100 text-slate-600",
  label: "Notification",
};

export const BELL_PREVIEW_LIMIT = 6;

export function getNotificationsPagePath(userRole = "student") {
  const base = userRole === "coach" ? "/coach" : userRole === "admin" ? "/admin" : "/student";
  return `${base}/notifications`;
}

export function getNotificationMeta(type, message = "") {
  if (type === "new_plan") {
    const isTraining = /entraînement|programme/i.test(message);
    return isTraining ? TYPE_META.new_training_plan : TYPE_META.new_diet_plan;
  }
  return TYPE_META[type] || DEFAULT_META;
}

export function resolveNotificationUrl(notification, userRole = "student") {
  if (notification?.action_url) {
    return notification.action_url;
  }

  const base = userRole === "coach" ? "/coach" : userRole === "admin" ? "/admin" : "/student";
  const { type, related_id: relatedId, message = "" } = notification;

  switch (type) {
    case "new_message":
      return relatedId ? `${base}/messages?with=${relatedId}` : `${base}/messages`;
    case "new_session":
    case "session_reminder":
    case "session_reminder_1h":
      return userRole === "coach" ? `${base}/calendar` : `${base}/sessions`;
    case "new_student":
      return relatedId ? `/coach/students/${relatedId}` : "/coach/students";
    case "checkin_new":
    case "daily_checkin":
      return "/coach/daily-checkins";
    case "checkin_comment":
    case "checkin_weekly":
    case "checkin_reminder":
      return "/student/progress";
    case "new_plan":
      return /entraînement|programme/i.test(message) ? "/student/training-plan" : "/student/diet-plan";
    case "new_diet_plan":
      return "/student/diet-plan";
    case "new_training_plan":
      return "/student/training-plan";
    case "workout_loads_tomorrow":
      return "/student/workout-log";
    case "photo_request":
    case "photo_reminder":
      return "/student/photos";
    case "support_reply":
      return `${base}/support`;
    case "admin_announcement":
      return `${base}/dashboard`;
    case "ai_coach":
      return "/student/ai-chat";
    case "payment":
    case "payment_received":
    case "payment_failed":
      return userRole === "coach" ? "/coach/payments" : "/student/payments";
    case "meal_log":
      return "/coach/meal-logs";
    case "supplement_plan":
      return "/student/supplements";
    default:
      return `${base}/dashboard`;
  }
}

export function formatRelativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "À l'instant";
  if (mins < 60) return `Il y a ${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `Il y a ${days} j`;
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}